/**
 * API client for Firebase callable functions
 * Includes automatic retry logic with exponential backoff for resilience
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { getUserTimezone } from './dates';
import { withRetry, type RetryOptions } from './retry';
import type {
  SubmitCheckinResponse,
  RecordCompletionResponse,
  AICoachMessageOutput,
  ChatMessageResponse,
  ChatContextToggles,
} from './types';

// Default retry configuration for API calls
const API_RETRY_OPTIONS: RetryOptions = {
  maxRetries: 3,
  initialDelayMs: 500,
  maxDelayMs: 5000,
  jitter: true,
};

// More aggressive retry for chat - must not fail
const CHAT_RETRY_OPTIONS: RetryOptions = {
  maxRetries: 4,
  initialDelayMs: 800,
  maxDelayMs: 8000,
  jitter: true,
};

// Callable function wrappers
const submitCheckinFn = httpsCallable<{
  mood: number;
  energy?: number;
  stress?: number;
  noteText?: string;
  noteSaved?: boolean;
  timezone?: string;
}, SubmitCheckinResponse>(functions, 'submitCheckin');

const recordCompletionFn = httpsCallable<{
  actionId: string;
  helped?: boolean | null;
  date?: string;
}, RecordCompletionResponse>(functions, 'recordCompletion');

/**
 * Submit a check-in and get back the plan
 * Uses retry logic for network resilience
 */
export async function submitCheckin(data: {
  mood: number;
  energy?: number;
  stress?: number;
  noteText?: string;
  noteSaved?: boolean;
}): Promise<SubmitCheckinResponse> {
  return withRetry(
    async () => {
      const result = await submitCheckinFn({
        ...data,
        timezone: getUserTimezone(),
      });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'submitCheckin' }
  );
}

/**
 * Record completion of an action
 * Uses retry logic for network resilience
 */
export async function recordCompletion(data: {
  actionId: string;
  helped?: boolean | null;
}): Promise<RecordCompletionResponse> {
  return withRetry(
    async () => {
      const result = await recordCompletionFn(data);
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'recordCompletion' }
  );
}

/**
 * Error type classification for UI handling
 */
export type ErrorType =
  | 'auth'        // User needs to re-authenticate
  | 'network'     // Network/connectivity issue
  | 'rate_limit'  // Too many requests
  | 'validation'  // Invalid input
  | 'not_found'   // Resource not found
  | 'permission'  // Permission denied
  | 'server'      // Server-side error
  | 'unknown';    // Unknown error

/**
 * Structured error result for comprehensive error handling
 */
export interface ErrorResult {
  message: string;     // User-friendly message
  type: ErrorType;     // Error classification
  isRetryable: boolean; // Whether the action can be retried
  originalError?: Error; // Original error for debugging
  /** HTTP status code when applicable (e.g. 429 for rate limit) */
  httpCode?: number;
  /** Error code from backend (e.g. resource-exhausted) */
  code?: string;
}

/**
 * Comprehensive error handling helper
 * Returns structured error information for UI handling
 */
export function parseError(error: unknown): ErrorResult {
  // Handle abort errors (user cancelled)
  if (error instanceof DOMException && error.name === 'AbortError') {
    return {
      message: 'Request was cancelled.',
      type: 'unknown',
      isRetryable: false,
    };
  }

  if (error instanceof Error) {
    const firebaseError = error as { code?: string; message: string; httpCode?: number };
    const code = firebaseError.code || '';
    const message = firebaseError.message.toLowerCase();

    // Authentication errors
    if (code === 'functions/unauthenticated' || message.includes('unauthenticated')) {
      return {
        message: 'Please sign in to continue.',
        type: 'auth',
        isRetryable: false,
        originalError: error,
      };
    }

    // Permission errors
    if (code === 'functions/permission-denied' || message.includes('permission')) {
      return {
        message: 'You don\'t have permission to do this.',
        type: 'permission',
        isRetryable: false,
        originalError: error,
      };
    }

    // Rate limiting
    if (code === 'functions/resource-exhausted' || message.includes('rate') || message.includes('too many') || firebaseError.httpCode === 429) {
      return {
        message: 'Too many requests. Take a breath and try again.',
        type: 'rate_limit',
        isRetryable: true,
        originalError: error,
        httpCode: firebaseError.httpCode ?? 429,
        code: code || 'resource-exhausted',
      };
    }

    // Validation errors
    if (code === 'functions/invalid-argument' || message.includes('invalid')) {
      return {
        message: 'Something wasn\'t quite right. Please try again.',
        type: 'validation',
        isRetryable: false,
        originalError: error,
      };
    }

    // Not found
    if (code === 'functions/not-found' || message.includes('not found')) {
      return {
        message: 'Something went missing. Try refreshing.',
        type: 'not_found',
        isRetryable: true,
        originalError: error,
      };
    }

    // Network errors
    if (message.includes('network') || message.includes('fetch') || message.includes('timeout') ||
        code === 'functions/unavailable' || code === 'functions/deadline-exceeded') {
      return {
        message: 'Network issue. Check your connection and try again.',
        type: 'network',
        isRetryable: true,
        originalError: error,
      };
    }

    // Server errors
    if (code === 'functions/internal' || message.includes('internal')) {
      return {
        message: 'Something went wrong on our end. Please try again.',
        type: 'server',
        isRetryable: true,
        originalError: error,
      };
    }

    // Default case
    return {
      message: 'Something didn\'t work. Please try again.',
      type: 'unknown',
      isRetryable: true,
      originalError: error,
    };
  }

  return {
    message: 'Something unexpected happened. Please try again.',
    type: 'unknown',
    isRetryable: true,
  };
}

/**
 * Simple error handling helper - returns just the message
 * For backward compatibility and simple cases
 */
export function getErrorMessage(error: unknown): string {
  return parseError(error).message;
}

// =============== AI Coach API ===============

import type { CoachContextToggles, GetCoachStartersResponse } from './types';

const aiCoachMessageFn = httpsCallable<{
  message: string;
  history?: Array<{ role: 'user' | 'assistant'; content: string }>;
  context?: { date?: string };
  toggles?: CoachContextToggles;
}, AICoachMessageOutput>(functions, 'aiCoachMessage');

/**
 * Send a message to the AI coach
 * Uses retry logic for network resilience
 */
export async function sendCoachMessage(
  message: string,
  history?: Array<{ role: 'user' | 'assistant'; content: string }>,
  context?: { date?: string },
  toggles?: CoachContextToggles
): Promise<AICoachMessageOutput> {
  return withRetry(
    async () => {
      const result = await aiCoachMessageFn({ message, history, context, toggles });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'sendCoachMessage' }
  );
}

const getCoachStartersFn = httpsCallable<{
  date?: string;
}, GetCoachStartersResponse>(functions, 'getCoachStarters');

/**
 * Get AI-generated smart conversation starters
 * Cached per user per day for efficiency
 */
export async function getCoachStarters(date?: string): Promise<GetCoachStartersResponse> {
  return withRetry(
    async () => {
      const result = await getCoachStartersFn({ date });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'getCoachStarters' }
  );
}

// =============== Baby Tracker Insights API ===============

export interface BabyTrackerInsightsStats {
  // Timeframe metadata - critical for clinical context
  dateRange: '24h' | '7d' | '30d';
  actualDaysTracked: number;
  oldestEventDate: string;
  newestEventDate: string;
  totalEventsInPeriod: number;

  // Feeding metrics
  totalFeedings: number;
  breastFeedings: number;
  bottleFeedings: number;
  solidFeedings: number;
  totalFeedingMins: number;
  totalBottleMl: number;
  avgFeedingsPerDay: string;
  avgHoursBetweenFeedings?: number;
  avgBottleMlPerFeed?: number;
  longestFeedingGapHours?: number;
  shortestFeedingGapHours?: number;

  // Pumping metrics
  totalPumpings: number;
  totalPumpingMins: number;
  totalPumpedMl: number;
  avgPumpMlPerSession?: number;

  // Diaper metrics
  totalDiapers: number;
  wetDiapers: number;
  dirtyDiapers: number;
  avgDiapersPerDay: string;
  avgWetPerDay?: string;
  avgDirtyPerDay?: string;

  // Clinical observations
  spitUpCount: number;
  fussyCount: number;
  milestoneCount: number;
  medicineCount: number;
  otherNotes: number;

  // Note details for context
  medicineNotes?: string[];
  milestoneNotes?: string[];
  fussyNotes?: string[];

  // Patient context
  babyAge?: string;
}

// Doctor mode output - clinical summary string
interface DoctorInsightsOutput {
  mode: 'doctor';
  insights: string;
}

// Parent mode output - structured highlights for UI
export interface ParentHighlights {
  steady: string;   // What's consistent/good
  changed: string;  // Notable changes or variations
  suggestion: string; // Actionable tip
}

export interface ParentInsightsOutput {
  mode: 'parent';
  highlights: ParentHighlights;
  rangeLabel: string;
  generatedAt: string;
  flags?: string[];  // Optional clinical flags
}

type BabyTrackerInsightsOutput = DoctorInsightsOutput | ParentInsightsOutput;

const babyTrackerInsightsFn = httpsCallable<{
  stats: BabyTrackerInsightsStats;
  mode?: 'doctor' | 'parent';
}, BabyTrackerInsightsOutput>(functions, 'babyTrackerInsights');

/**
 * Get AI-powered clinical insights for baby tracker data (doctor mode)
 * Uses retry logic for network resilience
 */
export async function getBabyTrackerInsights(
  stats: BabyTrackerInsightsStats
): Promise<DoctorInsightsOutput> {
  return withRetry(
    async () => {
      const result = await babyTrackerInsightsFn({ stats, mode: 'doctor' });
      return result.data as DoctorInsightsOutput;
    },
    { ...API_RETRY_OPTIONS, context: 'getBabyTrackerInsights' }
  );
}

/**
 * Get AI-powered parent highlights for baby tracker data
 * Returns structured JSON with 3 insights (steady, changed, suggestion)
 * Uses Firestore caching with 6-hour TTL
 */
export async function getBabyTrackerParentHighlights(
  stats: BabyTrackerInsightsStats
): Promise<ParentInsightsOutput> {
  return withRetry(
    async () => {
      const result = await babyTrackerInsightsFn({ stats, mode: 'parent' });
      return result.data as ParentInsightsOutput;
    },
    { ...API_RETRY_OPTIONS, context: 'getBabyTrackerParentHighlights' }
  );
}

// =============== Server-Authoritative Insights Metrics API ===============

/** Input for getInsightsMetrics callable */
export interface GetInsightsMetricsInput {
  periodDays?: 7 | 14 | 30;
  timezone?: string;
  babyId?: string;
  force?: boolean;
}

/** MetricValue with optional sufficiency signal */
export interface ServerMetricValue {
  value: number | null;
  insufficient?: boolean;
  reason?: 'insufficient_data' | 'mixed_units' | 'missing_fields' | 'no_events';
}

/** Trend comparison with significance */
export interface ServerTrend {
  current: number | null;
  previous: number | null;
  delta: number | null;
  deltaPercent: number | null;
  direction: 'up' | 'down' | 'stable';
  meaningful: boolean;
  insufficient?: boolean;
  reason?: string;
}

/** Hourly bucket for 24-hour rhythm */
export interface ServerHourlyBucket {
  hour: number;
  feedings: number;
  pumpings: number;
  diapers: number;
  sleeps: number;
  feedingMl: number;
  pumpedMl: number;
}

/** Server-computed InsightsMetrics response */
export interface InsightsMetricsResponse {
  version: 1;
  computedAt: number;
  period: {
    days: 7 | 14 | 30;
    startTs: number;
    endTs: number;
    timezone: string;
    label: string;
  };
  coverage: {
    daysWithData: number;
    daysTotal: number;
    eventCountsByType: {
      feeding: number;
      pumping: number;
      diaper: number;
      sleep: number;
      note: number;
    };
    totalEvents: number;
    lastEventTs: number | null;
  };
  sufficiency: {
    overallSufficient: boolean;
    feedingSufficient: boolean;
    diaperSufficient: boolean;
    sleepSufficient: boolean;
    pumpingSufficient: boolean;
    notesSufficient: boolean;
    minDaysRequired: number;
    minEventsPerDomain: Record<string, number>;
    warnings: string[];
  };
  atAGlance: {
    feedingsPerDay: ServerMetricValue;
    bottleMlPerDay: ServerMetricValue;
    nursingMinsPerDay: ServerMetricValue;
    pumpedMlPerDay: ServerMetricValue;
    diapersPerDay: ServerMetricValue;
    wetDiapersPerDay: ServerMetricValue;
    dirtyDiapersPerDay: ServerMetricValue;
    sleepHoursPerDay: ServerMetricValue;
    medianFeedingIntervalHours: ServerMetricValue;
    longestGapHours: ServerMetricValue;
  };
  trends: {
    feedingsPerDay: ServerTrend;
    bottleMlPerDay: ServerTrend;
    diapersPerDay: ServerTrend;
    sleepHoursPerDay: ServerTrend;
    pumpedMlPerDay: ServerTrend;
    nursingMinsPerDay: ServerTrend;
  };
  rhythm24h: {
    buckets: ServerHourlyBucket[];
    peakFeedingHours: number[];
    peakPumpingHours: number[];
    peakDiaperHours: number[];
    peakSleepHours: number[];
  };
  feedingMix: {
    breastCount: number;
    breastLeftCount: number;
    breastRightCount: number;
    breastBothCount: number;
    bottleCount: number;
    solidsCount: number;
    totalNursingMinutes: number;
    avgNursingMinutesPerSession: ServerMetricValue;
    totalBottleMl: number;
    avgBottleMlPerFeed: ServerMetricValue;
    breastPercent: number;
    bottlePercent: number;
    solidsPercent: number;
  };
  output: {
    wetCount: number;
    dirtyCount: number;
    bothCount: number;
    totalDiapers: number;
    diapersPerDay: ServerMetricValue;
    wetPerDay: ServerMetricValue;
    dirtyPerDay: ServerMetricValue;
    peakDiaperHours: number[];
  };
  notes: {
    spitUpCount: number;
    fussyCount: number;
    medicineCount: number;
    milestoneCount: number;
    otherCount: number;
    totalNotes: number;
    recentNotes: Array<{
      category: string;
      text?: string;
      timestampMs: number;
    }>;
  };
  intervalsGaps: {
    medianIntervalHours: ServerMetricValue;
    avgIntervalHours: ServerMetricValue;
    longestGapHours: ServerMetricValue;
    longestGapStartTs: number | null;
    longestGapEndTs: number | null;
    intervalDistribution?: {
      under2h: number;
      twoTo3h: number;
      threeToFourH: number;
      over4h: number;
    };
  };
  sleep: {
    totalSleepMinutes: number;
    napMinutes: number;
    nightSleepMinutes: number;
    sleepEventsCount: number;
    avgSleepHoursPerDay: ServerMetricValue;
    longestSleepStretchMinutes: ServerMetricValue;
  };
}

const getInsightsMetricsFn = httpsCallable<GetInsightsMetricsInput, InsightsMetricsResponse>(
  functions,
  'getInsightsMetrics'
);

/**
 * Get server-computed insights metrics for baby tracker data
 * Uses caching (15min TTL) and rate limiting (30/min)
 */
export async function getInsightsMetrics(
  input: GetInsightsMetricsInput = {}
): Promise<InsightsMetricsResponse> {
  return withRetry(
    async () => {
      const result = await getInsightsMetricsFn({
        ...input,
        timezone: input.timezone || getUserTimezone(),
      });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'getInsightsMetrics' }
  );
}

// =============== Daily Action Plan API ===============

import type {
  GetDailyActionPlanResponse,
  CompleteDailyActionItemResponse,
  SwapDailyActionResponse,
  AddMoreActionsResponse,
} from './types';

const getDailyActionPlanFn = httpsCallable<{
  date?: string;
  forceRegenerate?: boolean;
}, GetDailyActionPlanResponse>(functions, 'getDailyActionPlan');

const completeDailyActionItemFn = httpsCallable<{
  date: string;
  itemId: string;
}, CompleteDailyActionItemResponse>(functions, 'completeDailyActionItem');

/**
 * Get or generate the daily action plan
 * Uses retry logic for network resilience
 */
export async function getDailyActionPlan(
  date?: string,
  forceRegenerate?: boolean
): Promise<GetDailyActionPlanResponse> {
  return withRetry(
    async () => {
      const result = await getDailyActionPlanFn({ date, forceRegenerate });
      // Convert Firestore timestamps to Dates
      const plan = result.data.plan;
      return {
        ...result.data,
        plan: {
          ...plan,
          generatedAt: (plan.generatedAt as unknown as { toDate?: () => Date })?.toDate?.() || new Date(plan.generatedAt as unknown as string),
          completedAt: plan.completedAt
            ? (plan.completedAt as unknown as { toDate?: () => Date })?.toDate?.() || new Date(plan.completedAt as unknown as string)
            : undefined,
          items: plan.items.map(item => ({
            ...item,
            completedAt: item.completedAt
              ? (item.completedAt as unknown as { toDate?: () => Date })?.toDate?.() || new Date(item.completedAt as unknown as string)
              : undefined,
          })),
        },
      };
    },
    { ...API_RETRY_OPTIONS, context: 'getDailyActionPlan' }
  );
}

/**
 * Mark a daily action item as completed
 * Uses retry logic for network resilience
 */
export async function completeDailyActionItem(
  date: string,
  itemId: string
): Promise<CompleteDailyActionItemResponse> {
  return withRetry(
    async () => {
      const result = await completeDailyActionItemFn({ date, itemId });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'completeDailyActionItem' }
  );
}

const swapDailyActionFn = httpsCallable<{
  date: string;
  itemId: string;
}, SwapDailyActionResponse>(functions, 'swapDailyAction');

const addMoreActionsFn = httpsCallable<{
  date: string;
  count?: number;
}, AddMoreActionsResponse>(functions, 'addMoreActions');

/**
 * Swap a daily action item with a new AI-generated one
 * Uses retry logic for network resilience
 */
export async function swapDailyAction(
  date: string,
  itemId: string
): Promise<SwapDailyActionResponse> {
  return withRetry(
    async () => {
      const result = await swapDailyActionFn({ date, itemId });
      // Convert timestamp to Date if present
      const newItem = result.data.newItem;
      return {
        ...result.data,
        newItem: {
          ...newItem,
          completedAt: newItem.completedAt ? new Date(newItem.completedAt as unknown as string) : undefined,
        },
      };
    },
    { ...API_RETRY_OPTIONS, context: 'swapDailyAction' }
  );
}

/**
 * Add more actions to the daily plan
 * Uses retry logic for network resilience
 */
export async function addMoreActions(
  date: string,
  count?: number
): Promise<AddMoreActionsResponse> {
  return withRetry(
    async () => {
      const result = await addMoreActionsFn({ date, count });
      // Convert timestamps to Dates
      return {
        ...result.data,
        newItems: result.data.newItems.map(item => ({
          ...item,
          completedAt: item.completedAt ? new Date(item.completedAt as unknown as string) : undefined,
        })),
      };
    },
    { ...API_RETRY_OPTIONS, context: 'addMoreActions' }
  );
}

// =============== Today Plan API (Baby-first daily planning) ===============

import type {
  GetTodayPlanResponse,
  CompleteTodayPlanItemResponse,
} from './types';

const getTodayPlanFn = httpsCallable<{
  date?: string;
  force?: boolean;
}, GetTodayPlanResponse>(functions, 'getTodayPlan');

const completeTodayPlanItemFn = httpsCallable<{
  date: string;
  itemId: string;
}, CompleteTodayPlanItemResponse>(functions, 'completeTodayPlanItem');

/**
 * Get or generate the Today Plan (baby-first daily planning)
 * Uses retry logic for network resilience
 */
export async function getTodayPlan(
  date?: string,
  force?: boolean
): Promise<GetTodayPlanResponse> {
  return withRetry(
    async () => {
      const result = await getTodayPlanFn({ date, force });
      // Convert Firestore timestamps to Dates
      const plan = result.data.plan;
      return {
        ...result.data,
        plan: {
          ...plan,
          generatedAt: plan.generatedAt ? new Date(plan.generatedAt as unknown as string) : new Date(),
          sections: plan.sections.map(section => ({
            ...section,
            items: section.items.map(item => ({
              ...item,
              doneAt: item.doneAt ? new Date(item.doneAt as unknown as string) : undefined,
            })),
          })),
        },
      };
    },
    { ...API_RETRY_OPTIONS, context: 'getTodayPlan' }
  );
}

/**
 * Mark a Today Plan item as completed
 * Uses retry logic for network resilience
 */
export async function completeTodayPlanItem(
  date: string,
  itemId: string
): Promise<CompleteTodayPlanItemResponse> {
  return withRetry(
    async () => {
      const result = await completeTodayPlanItemFn({ date, itemId });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'completeTodayPlanItem' }
  );
}

// =============== Mindfulness Moments API ===============

import type {
  GetMindfulnessMomentsResponse,
  SwapMindfulnessMomentResponse,
  CompleteMindfulnessMomentResponse,
  MindfulnessCompletionFeedback,
} from './types';

const getMindfulnessMomentsFn = httpsCallable<{
  date?: string;
  timezone?: string;
}, GetMindfulnessMomentsResponse>(functions, 'getMindfulnessMoments');

const swapMindfulnessMomentFn = httpsCallable<{
  activityId: string;
  date?: string;
  timezone?: string;
}, SwapMindfulnessMomentResponse>(functions, 'swapMindfulnessMoment');

const completeMindfulnessMomentFn = httpsCallable<{
  activityId: string;
  feedback?: MindfulnessCompletionFeedback;
  date?: string;
  timezone?: string;
}, CompleteMindfulnessMomentResponse>(functions, 'completeMindfulnessMoment');

/**
 * Get or generate mindfulness moments for the day
 * Uses retry logic for network resilience
 */
export async function getMindfulnessMoments(
  date?: string
): Promise<GetMindfulnessMomentsResponse> {
  return withRetry(
    async () => {
      const result = await getMindfulnessMomentsFn({ date, timezone: getUserTimezone() });
      // Convert Firestore timestamps to Dates
      const moments = result.data.moments;
      return {
        ...result.data,
        moments: {
          ...moments,
          generatedAt: (moments.generatedAt as unknown as { toDate?: () => Date })?.toDate?.() || new Date(moments.generatedAt as unknown as string),
          activities: moments.activities.map(activity => ({
            ...activity,
            completedAt: activity.completedAt
              ? (activity.completedAt as unknown as { toDate?: () => Date })?.toDate?.() || new Date(activity.completedAt as unknown as string)
              : undefined,
          })),
        },
      };
    },
    { ...API_RETRY_OPTIONS, context: 'getMindfulnessMoments' }
  );
}

/**
 * Swap a mindfulness activity with a fresh alternative
 * Uses retry logic for network resilience
 */
export async function swapMindfulnessMoment(
  activityId: string,
  date?: string
): Promise<SwapMindfulnessMomentResponse> {
  return withRetry(
    async () => {
      const result = await swapMindfulnessMomentFn({ activityId, date, timezone: getUserTimezone() });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'swapMindfulnessMoment' }
  );
}

/**
 * Mark a mindfulness activity as complete with optional feedback
 * Uses retry logic for network resilience
 */
export async function completeMindfulnessMoment(
  activityId: string,
  feedback?: MindfulnessCompletionFeedback,
  date?: string
): Promise<CompleteMindfulnessMomentResponse> {
  return withRetry(
    async () => {
      const result = await completeMindfulnessMomentFn({ activityId, feedback, date, timezone: getUserTimezone() });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'completeMindfulnessMoment' }
  );
}

// =============== Personalized Reset API ===============

import type {
  GenerateMindfulnessResetInput,
  GenerateMindfulnessResetOutput,
  PersonalizedResetGoal,
  PersonalizedResetDuration,
  PersonalizedResetMood,
} from './types';

const generateMindfulnessResetFn = httpsCallable<
  GenerateMindfulnessResetInput,
  GenerateMindfulnessResetOutput
>(functions, 'generateMindfulnessReset');

/**
 * Generate a personalized reset session
 * User-initiated only - uses fast/cheap AI model with caching
 */
export async function generatePersonalizedReset(
  goal: PersonalizedResetGoal,
  durationSeconds: PersonalizedResetDuration,
  mood?: PersonalizedResetMood
): Promise<GenerateMindfulnessResetOutput> {
  return withRetry(
    async () => {
      const result = await generateMindfulnessResetFn({
        goal,
        durationSeconds,
        mood,
        timezone: getUserTimezone(),
      });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'generatePersonalizedReset' }
  );
}

// =============== Important Documents API ===============

import type {
  UploadDocumentResponse,
  GetDocumentsResponse,
  GetDocumentResponse,
  GetDocumentViewUrlResponse,
  DocumentType,
  SearchDocumentsResponse,
} from './types';

const uploadDocumentFn = httpsCallable<{
  fileName: string;
  mimeType: string;
  fileSize: number;
  babyId?: string;
}, UploadDocumentResponse>(functions, 'uploadDocument');

const confirmDocumentUploadFn = httpsCallable<{
  documentId: string;
  babyId?: string;
}, { documentId: string; status: string }>(functions, 'confirmDocumentUpload');

const getDocumentsFn = httpsCallable<{
  babyId?: string;
  docTypes?: DocumentType[];
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}, GetDocumentsResponse>(functions, 'getDocuments');

const getDocumentFn = httpsCallable<{
  documentId: string;
}, GetDocumentResponse>(functions, 'getDocument');

const getDocumentViewUrlFn = httpsCallable<{
  documentId: string;
}, GetDocumentViewUrlResponse>(functions, 'getDocumentViewUrl');

const updateDocumentFn = httpsCallable<{
  documentId: string;
  title?: string;
  docType?: DocumentType;
  documentDate?: string;
  babyId?: string;
  tags?: string[];
  notes?: string;
  visibleInChat?: boolean;
}, { documentId: string; updatedFields: string[] }>(functions, 'updateDocument');

const deleteDocumentFn = httpsCallable<{
  documentId: string;
}, { success: boolean }>(functions, 'deleteDocument');

const searchDocumentsFn = httpsCallable<{
  query: string;
  babyId?: string;
  docTypes?: DocumentType[];
  limit?: number;
}, SearchDocumentsResponse>(functions, 'searchDocuments');

/**
 * Request a signed upload URL for a new document
 */
export async function uploadDocument(data: {
  fileName: string;
  mimeType: string;
  fileSize: number;
  babyId?: string;
}): Promise<UploadDocumentResponse> {
  return withRetry(
    async () => {
      const result = await uploadDocumentFn(data);
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'uploadDocument' }
  );
}

/**
 * Confirm document upload is complete and trigger processing
 */
export async function confirmDocumentUpload(
  documentId: string,
  babyId?: string
): Promise<{ documentId: string; status: string }> {
  return withRetry(
    async () => {
      const result = await confirmDocumentUploadFn({ documentId, babyId });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'confirmDocumentUpload' }
  );
}

/**
 * Get documents with optional filtering
 */
export async function getDocuments(filters?: {
  babyId?: string;
  docTypes?: DocumentType[];
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}): Promise<GetDocumentsResponse> {
  return withRetry(
    async () => {
      const result = await getDocumentsFn(filters || {});
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'getDocuments' }
  );
}

/**
 * Get a single document with its facts
 */
export async function getDocument(documentId: string): Promise<GetDocumentResponse> {
  return withRetry(
    async () => {
      const result = await getDocumentFn({ documentId });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'getDocument' }
  );
}

/**
 * Get a signed URL for viewing/downloading a document
 */
export async function getDocumentViewUrl(documentId: string): Promise<GetDocumentViewUrlResponse> {
  return withRetry(
    async () => {
      const result = await getDocumentViewUrlFn({ documentId });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'getDocumentViewUrl' }
  );
}

/**
 * Update document metadata
 */
export async function updateDocumentApi(
  documentId: string,
  updates: {
    title?: string;
    docType?: DocumentType;
    documentDate?: string;
    babyId?: string;
    tags?: string[];
    notes?: string;
    visibleInChat?: boolean;
  }
): Promise<{ documentId: string; updatedFields: string[] }> {
  return withRetry(
    async () => {
      const result = await updateDocumentFn({ documentId, ...updates });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'updateDocument' }
  );
}

/**
 * Delete a document
 */
export async function deleteDocumentApi(documentId: string): Promise<{ success: boolean }> {
  return withRetry(
    async () => {
      const result = await deleteDocumentFn({ documentId });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'deleteDocument' }
  );
}

/**
 * Search documents by keyword
 */
export async function searchDocumentsApi(
  query: string,
  filters?: {
    babyId?: string;
    docTypes?: DocumentType[];
    limit?: number;
  }
): Promise<SearchDocumentsResponse> {
  return withRetry(
    async () => {
      const result = await searchDocumentsFn({ query, ...filters });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'searchDocuments' }
  );
}

// =============== Document Context Retrieval API ===============

export interface ContextSnippet {
  documentId: string;
  documentTitle: string;
  docType: string;
  pageNumber?: number;
  text: string;
  score: number;
  providerName?: string;
  documentDate?: string;
}

export interface RetrieveDocumentContextResponse {
  snippets: ContextSnippet[];
  totalChunksSearched: number;
  citations: Array<{
    documentId: string;
    title: string;
    date?: string;
  }>;
}

const retrieveDocumentContextFn = httpsCallable<{
  query: string;
  babyId?: string;
  maxChunks?: number;
  maxTokens?: number;
}, RetrieveDocumentContextResponse>(functions, 'retrieveDocumentContext');

/**
 * Retrieve relevant document context for a query (RAG search)
 * Used for Doctor Q&A relevant documents and chat context
 */
export async function retrieveDocumentContext(
  query: string,
  options?: {
    babyId?: string;
    maxChunks?: number;
    maxTokens?: number;
  }
): Promise<RetrieveDocumentContextResponse> {
  return withRetry(
    async () => {
      const result = await retrieveDocumentContextFn({ query, ...options });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'retrieveDocumentContext' }
  );
}

// =============== Chat API ===============

const sendChatMessageFn = httpsCallable<{
  sessionId: string;
  message: string;
  clientMessageId?: string;
  assistantClientMessageId?: string;
  toggles?: ChatContextToggles;
}, ChatMessageResponse>(functions, 'sendChatMessage');

/**
 * Send a message to the persistent chat
 * Server-authoritative: history is loaded from Firestore, not sent by client
 * Uses aggressive retry logic - chat must not fail.
 * Idempotency: always pass clientMessageId and assistantClientMessageId from the client;
 * the server returns a cached response for duplicate clientMessageId, so retries never duplicate messages.
 */
export async function sendChatMessage(data: {
  sessionId: string;
  message: string;
  clientMessageId?: string;
  assistantClientMessageId?: string;
  toggles?: ChatContextToggles;
}): Promise<ChatMessageResponse> {
  return withRetry(
    async () => {
      const result = await sendChatMessageFn(data);
      return result.data;
    },
    { ...CHAT_RETRY_OPTIONS, context: 'sendChatMessage' }
  );
}

// =============== Household Claims API ===============

const syncHouseholdClaimFn = httpsCallable<void, { synced: boolean; householdId: string | null }>(
  functions,
  'syncHouseholdClaim'
);

/**
 * Sync household claim for the current user
 * Call this when storage permissions fail or after login for partnered users
 * Returns true if the claim was updated (requires token refresh)
 */
export async function syncHouseholdClaim(): Promise<{ synced: boolean; householdId: string | null }> {
  const result = await syncHouseholdClaimFn();
  return result.data;
}

// =============== Learning API (Bite-Sized Parenting Lessons) ===============

import type {
  GetLearningPlanResponse,
  SwapLearningLessonResponse,
  CompleteLearningLessonResponse,
  GetLearningHistoryResponse,
  LearningHelpfulRating,
  LearningFeedbackTag,
} from './types';

const getLearningPlanFn = httpsCallable<{
  date?: string;
  timezone?: string;
}, GetLearningPlanResponse>(functions, 'getLearningPlan');

const swapLearningLessonFn = httpsCallable<{
  lessonId: string;
  date?: string;
  timezone?: string;
}, SwapLearningLessonResponse>(functions, 'swapLearningLesson');

const completeLearningLessonFn = httpsCallable<{
  lessonId: string;
  helpful?: LearningHelpfulRating;
  date?: string;
  timezone?: string;
  // Structured feedback fields
  feedbackTags?: LearningFeedbackTag[];
  feedbackText?: string;
  readSeconds?: number;
}, CompleteLearningLessonResponse>(functions, 'completeLearningLesson');

/**
 * Get or generate learning plan for the day
 * Uses retry logic for network resilience
 */
export async function getLearningPlan(
  date?: string
): Promise<GetLearningPlanResponse> {
  return withRetry(
    async () => {
      const result = await getLearningPlanFn({ date, timezone: getUserTimezone() });
      // Convert Firestore timestamps to Dates
      const plan = result.data.plan;
      return {
        ...result.data,
        plan: {
          ...plan,
          generatedAt: (plan.generatedAt as unknown as { toDate?: () => Date })?.toDate?.() || new Date(plan.generatedAt as unknown as string),
          lessons: plan.lessons.map(lesson => ({
            ...lesson,
            completedAt: lesson.completedAt
              ? (lesson.completedAt as unknown as { toDate?: () => Date })?.toDate?.() || new Date(lesson.completedAt as unknown as string)
              : undefined,
          })),
        },
      };
    },
    { ...API_RETRY_OPTIONS, context: 'getLearningPlan' }
  );
}

/**
 * Swap a learning lesson with a fresh alternative
 * Uses retry logic for network resilience
 */
export async function swapLearningLesson(
  lessonId: string,
  date?: string
): Promise<SwapLearningLessonResponse> {
  return withRetry(
    async () => {
      const result = await swapLearningLessonFn({ lessonId, date, timezone: getUserTimezone() });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'swapLearningLesson' }
  );
}

/** Structured feedback options for learning lesson completion */
export interface CompleteLearningLessonOptions {
  helpful?: LearningHelpfulRating;
  date?: string;
  feedbackTags?: LearningFeedbackTag[];
  feedbackText?: string;
  readSeconds?: number;
}

/**
 * Mark a learning lesson as complete with optional rating and structured feedback
 * Uses retry logic for network resilience
 */
export async function completeLearningLesson(
  lessonId: string,
  options?: CompleteLearningLessonOptions
): Promise<CompleteLearningLessonResponse> {
  return withRetry(
    async () => {
      const result = await completeLearningLessonFn({
        lessonId,
        helpful: options?.helpful,
        date: options?.date,
        timezone: getUserTimezone(),
        feedbackTags: options?.feedbackTags,
        feedbackText: options?.feedbackText,
        readSeconds: options?.readSeconds,
      });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'completeLearningLesson' }
  );
}

const getLearningHistoryFn = httpsCallable<{
  limit?: number;
}, GetLearningHistoryResponse>(functions, 'getLearningHistory');

/**
 * Get learning history (completed lessons from recent days)
 * Uses retry logic for network resilience
 */
export async function getLearningHistory(
  limit?: number
): Promise<GetLearningHistoryResponse> {
  return withRetry(
    async () => {
      const result = await getLearningHistoryFn({ limit });
      // Convert ISO date strings to Date objects
      return {
        ...result.data,
        days: result.data.days.map(day => ({
          ...day,
          completedLessons: day.completedLessons.map(lesson => ({
            ...lesson,
            completedAt: new Date(lesson.completedAt as unknown as string),
          })),
        })),
      };
    },
    { ...API_RETRY_OPTIONS, context: 'getLearningHistory' }
  );
}

// =============== Learning Bookmarks API (Library Feature) ===============

import type {
  LearningBookmark,
  AddLearningBookmarkResponse,
  RemoveLearningBookmarkResponse,
  GetLearningBookmarksResponse,
  LearningLesson,
} from './types';

const addLearningBookmarkFn = httpsCallable<{
  lesson: {
    id: string;
    title: string;
    category: string;
    teaser: string;
    durationMinutes: number;
    keyPoints: string[];
    practicalTip: string;
    whyItMatters: string;
    reasonForYou?: string;
  };
}, AddLearningBookmarkResponse>(functions, 'addLearningBookmark');

const removeLearningBookmarkFn = httpsCallable<{
  lessonId: string;
}, RemoveLearningBookmarkResponse>(functions, 'removeLearningBookmark');

const getLearningBookmarksFn = httpsCallable<{
  limit?: number;
}, GetLearningBookmarksResponse>(functions, 'getLearningBookmarks');

/**
 * Add a lesson to bookmarks (Library)
 * Idempotent: re-adding updates savedAt timestamp
 */
export async function addLearningBookmark(
  lesson: LearningLesson
): Promise<AddLearningBookmarkResponse> {
  return withRetry(
    async () => {
      const result = await addLearningBookmarkFn({
        lesson: {
          id: lesson.id,
          title: lesson.title,
          category: lesson.category,
          teaser: lesson.teaser,
          durationMinutes: lesson.durationMinutes,
          keyPoints: lesson.keyPoints,
          practicalTip: lesson.practicalTip,
          whyItMatters: lesson.whyItMatters,
          reasonForYou: lesson.reasonForYou,
        },
      });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'addLearningBookmark' }
  );
}

/**
 * Remove a lesson from bookmarks
 * Idempotent: removing non-existent bookmark succeeds silently
 */
export async function removeLearningBookmark(
  lessonId: string
): Promise<RemoveLearningBookmarkResponse> {
  return withRetry(
    async () => {
      const result = await removeLearningBookmarkFn({ lessonId });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'removeLearningBookmark' }
  );
}

/**
 * Get all bookmarked lessons for Library
 * Returns most recently saved first
 */
export async function getLearningBookmarks(
  limit?: number
): Promise<GetLearningBookmarksResponse> {
  return withRetry(
    async () => {
      const result = await getLearningBookmarksFn({ limit });
      // Convert Firestore timestamps to Dates
      return {
        ...result.data,
        bookmarks: result.data.bookmarks.map((bookmark: LearningBookmark) => ({
          ...bookmark,
          savedAt: new Date((bookmark.savedAt as unknown as { _seconds: number })._seconds * 1000),
        })),
      };
    },
    { ...API_RETRY_OPTIONS, context: 'getLearningBookmarks' }
  );
}

// =============== Milestones 2.0 API ===============

import type {
  GetMilestonePeriodInput,
  GetMilestonePeriodResponse,
  RefreshMilestonePeriodResponse,
} from './types';

const getMilestonePeriodFn = httpsCallable<
  GetMilestonePeriodInput,
  GetMilestonePeriodResponse
>(functions, 'getMilestonePeriodV2');

const refreshMilestonePeriodFn = httpsCallable<
  GetMilestonePeriodInput,
  RefreshMilestonePeriodResponse
>(functions, 'refreshMilestonePeriod');

/**
 * Get milestone period content (AI-generated developmental guidance)
 * Uses retry logic for network resilience
 */
export async function getMilestonePeriod(
  input: GetMilestonePeriodInput = {}
): Promise<GetMilestonePeriodResponse> {
  return withRetry(
    async () => {
      const result = await getMilestonePeriodFn(input);
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'getMilestonePeriod' }
  );
}

/**
 * Force refresh milestone period content (bypasses cache)
 * Rate limited to 3 refreshes per day
 */
export async function refreshMilestonePeriod(
  input: GetMilestonePeriodInput = {}
): Promise<RefreshMilestonePeriodResponse> {
  return withRetry(
    async () => {
      const result = await refreshMilestonePeriodFn(input);
      return result.data;
    },
    { ...API_RETRY_OPTIONS, maxRetries: 1, context: 'refreshMilestonePeriod' }
  );
}

// =============== Doctor Q&A Visit Brief API ===============

import type {
  DoctorQaGenerateVisitBriefInput,
  DoctorQaGenerateVisitBriefOutput,
} from './types';

const generateVisitBriefFn = httpsCallable<
  DoctorQaGenerateVisitBriefInput,
  DoctorQaGenerateVisitBriefOutput
>(functions, 'doctorQaGenerateVisitBrief');

/**
 * Generate a visit brief for doctor appointments
 * Combines open questions with recent tracking data
 */
export async function generateDoctorQaVisitBrief(
  input: DoctorQaGenerateVisitBriefInput
): Promise<DoctorQaGenerateVisitBriefOutput> {
  return withRetry(
    async () => {
      const result = await generateVisitBriefFn(input);
      return result.data;
    },
    { ...API_RETRY_OPTIONS, maxRetries: 2, context: 'generateDoctorQaVisitBrief' }
  );
}

// =============== Love Maps API ===============

import type {
  GetLoveMapsDeckResponse,
  SubmitLoveMapsResponseInput,
  SubmitLoveMapsResponseResponse,
  LoveMapsDailyDeckDoc,
  LoveMapsCard,
  LoveMapsCardRating,
  // V2 Types
  RecordLoveMapsCardActionInput,
  RecordLoveMapsCardActionOutput,
  AppendLoveMapsCardsInput,
  AppendLoveMapsCardsOutput,
} from './types';

/** Response from getLoveMapsDailyDeck when deck is generating */
export interface GetLoveMapsDeckApiResponse {
  state: 'ready' | 'generating' | 'failed';
  deck?: LoveMapsDailyDeckDoc;
  responses?: GetLoveMapsDeckResponse['responses'];
  partnerResponses?: GetLoveMapsDeckResponse['partnerResponses'];
  isNew?: boolean;
  error?: string;
  /** Included when state='generating' for client Firestore subscription */
  householdId?: string;
}

/** Response from swapLoveMapsCard */
export interface SwapLoveMapsCardResponse {
  deck: LoveMapsDailyDeckDoc;
  newCard: LoveMapsCard;
}

/** Response from rateLoveMapsCard */
export interface RateLoveMapsCardResponse {
  responseId: string;
}

const getLoveMapsDailyDeckFn = httpsCallable<{
  dateId?: string;
  timezone?: string;
}, GetLoveMapsDeckApiResponse>(functions, 'getLoveMapsDailyDeck');

const submitLoveMapsResponseFn = httpsCallable<
  SubmitLoveMapsResponseInput & { timezone?: string },
  SubmitLoveMapsResponseResponse
>(functions, 'submitLoveMapsResponse');

const rateLoveMapsCardFn = httpsCallable<{
  cardId: string;
  rating: LoveMapsCardRating;
  dateId?: string;
  timezone?: string;
}, RateLoveMapsCardResponse>(functions, 'rateLoveMapsCard');

const swapLoveMapsCardFn = httpsCallable<{
  cardId: string;
  dateId?: string;
  timezone?: string;
}, SwapLoveMapsCardResponse>(functions, 'swapLoveMapsCard');

/**
 * Get the Love Maps daily deck for a household
 * Returns deck state: 'ready', 'generating', or 'failed'
 */
export async function getLoveMapsDailyDeck(
  dateId?: string
): Promise<GetLoveMapsDeckApiResponse> {
  return withRetry(
    async () => {
      const result = await getLoveMapsDailyDeckFn({
        dateId,
        timezone: getUserTimezone(),
      });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'getLoveMapsDailyDeck' }
  );
}

/**
 * Submit or update a Love Maps response (answer/discussed/rating)
 */
export async function submitLoveMapsResponse(
  input: SubmitLoveMapsResponseInput
): Promise<SubmitLoveMapsResponseResponse> {
  return withRetry(
    async () => {
      const result = await submitLoveMapsResponseFn({
        ...input,
        timezone: getUserTimezone(),
      });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'submitLoveMapsResponse' }
  );
}

/**
 * Rate a Love Maps card (thumbs up/down/skip)
 * Also updates household preferences for adaptive generation
 */
export async function rateLoveMapsCard(
  cardId: string,
  rating: LoveMapsCardRating,
  dateId?: string
): Promise<RateLoveMapsCardResponse> {
  return withRetry(
    async () => {
      const result = await rateLoveMapsCardFn({
        cardId,
        rating,
        dateId,
        timezone: getUserTimezone(),
      });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'rateLoveMapsCard' }
  );
}

/**
 * Swap a Love Maps card for a new one
 * Replaces card in deck with same-category alternative
 * @deprecated V2: Use recordLoveMapsCardAction with action='skipped' instead
 */
export async function swapLoveMapsCard(
  cardId: string,
  dateId?: string
): Promise<SwapLoveMapsCardResponse> {
  return withRetry(
    async () => {
      const result = await swapLoveMapsCardFn({
        cardId,
        dateId,
        timezone: getUserTimezone(),
      });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'swapLoveMapsCard' }
  );
}

// =============== Love Maps V2 API ===============

const recordLoveMapsCardActionFn = httpsCallable<
  RecordLoveMapsCardActionInput & { timezone?: string },
  RecordLoveMapsCardActionOutput
>(functions, 'recordLoveMapsCardAction');

const appendLoveMapsCardsFn = httpsCallable<
  AppendLoveMapsCardsInput & { timezone?: string },
  AppendLoveMapsCardsOutput
>(functions, 'appendLoveMapsCards');

/**
 * V2: Record a swipe action on a Love Maps card
 * Single endpoint that replaces submitLoveMapsResponse + rateLoveMapsCard
 * Idempotent - safe to retry on network failures
 */
export async function recordLoveMapsCardAction(
  input: RecordLoveMapsCardActionInput
): Promise<RecordLoveMapsCardActionOutput> {
  return withRetry(
    async () => {
      const result = await recordLoveMapsCardActionFn({
        ...input,
        timezone: getUserTimezone(),
      });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'recordLoveMapsCardAction' }
  );
}

/**
 * V2: Append more cards to the current Love Maps deck
 * Used for infinite-ish swipe sessions - fetches additional cards without reloading
 * Returns the newly appended cards and total count
 */
export async function appendLoveMapsCards(
  input?: AppendLoveMapsCardsInput
): Promise<AppendLoveMapsCardsOutput> {
  return withRetry(
    async () => {
      const result = await appendLoveMapsCardsFn({
        ...input,
        timezone: getUserTimezone(),
      });
      return result.data;
    },
    { ...API_RETRY_OPTIONS, context: 'appendLoveMapsCards' }
  );
}